/** Admin — B2B Partners: pending applications + active partners management. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Check, X, KeyRound, Power, FileText, Mail, Landmark, Loader2, ExternalLink, Trash2 } from "lucide-react";
import api from "@/lib/api";

const FIELD = "w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] outline-none";
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" · ");
  return "";
};

const BILLING = { monthly_invoice: "Monthly Invoice", pay_in_vehicle: "Guest Pays in Vehicle" };

const CONTRACT_BADGE = {
  none: { label: "Nije poslan", cls: "border-zinc-600 text-zinc-400" },
  Draft: { label: "Nije poslan", cls: "border-zinc-600 text-zinc-400" },
  Sent: { label: "Poslan", cls: "border-amber-400/50 text-amber-300" },
  Signed: { label: "Potpisan", cls: "border-emerald-400/50 text-emerald-300" },
  Expired: { label: "Istekao", cls: "border-red-400/50 text-red-300" },
};

export default function B2BPartnersPanel() {
  const [sub, setSub] = useState("pending");
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [approving, setApproving] = useState(null); // lead being approved
  const [adding, setAdding] = useState(false);
  const [billingPartner, setBillingPartner] = useState(null);
  const [contractBusy, setContractBusy] = useState(null);
  const [deleting, setDeleting] = useState(null); // partner pending delete confirm
  const [deleteBusy, setDeleteBusy] = useState(false);

  const deletePartner = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/partners/${deleting.id}`);
      toast.success(`Partner ${deleting.company_name} trajno obrisan`);
      setDeleting(null);
      load();
    } catch (e) { toast.error(errMsg(e) || "Brisanje nije uspjelo"); }
    finally { setDeleteBusy(false); }
  };

  const sendContract = async (p) => {
    setContractBusy(p.id + "send");
    try {
      const { data } = await api.post(`/admin/partners/${p.id}/contract/send`);
      toast.success(`Ugovor poslan na ${data.sent_to}`);
      load();
    } catch (e) { toast.error(errMsg(e) || "Slanje ugovora nije uspjelo"); }
    finally { setContractBusy(null); }
  };

  const viewContract = async (p) => {
    setContractBusy(p.id + "view");
    try {
      const { data } = await api.get(`/admin/partners/${p.id}/contract/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF nije dostupan"); }
    finally { setContractBusy(null); }
  };

  const markContractSigned = async (p) => {
    setContractBusy(p.id + "mark");
    try {
      await api.post(`/admin/partners/${p.id}/contract/mark-signed`);
      toast.success("Ugovor označen kao potpisan");
      load();
    } catch (e) { toast.error(errMsg(e) || "Nije uspjelo"); }
    finally { setContractBusy(null); }
  };

  const load = async () => {
    try {
      const [l, p] = await Promise.all([api.get("/admin/b2b-leads"), api.get("/admin/partners")]);
      setLeads(l.data || []); setPartners(p.data || []);
    } catch { /* soft */ }
  };
  useEffect(() => { load(); }, []);

  const pending = leads.filter((l) => l.status === "pending");

  const reject = async (id) => {
    await api.post(`/admin/b2b-leads/${id}/reject`); toast.success("Application rejected"); load();
  };
  const resetPin = async (p) => {
    try {
      const { data } = await api.post(`/admin/partners/${p.id}/reset-pin`, {});
      toast.success(`New PIN ${data.pin} emailed to ${p.email}`);
    } catch (e) { toast.error(errMsg(e) || "Reset failed"); }
  };
  const toggleActive = async (p) => {
    await api.patch(`/admin/partners/${p.id}`, { active: !p.active });
    toast.success(p.active ? "Partner deactivated" : "Partner activated"); load();
  };

  return (
    <div data-testid="b2b-partners-panel" className="mt-10">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-4 h-4 text-[#C5A059]" />
        <h2 className="text-xs uppercase tracking-[0.25em] text-zinc-400">B2B Partners — Hotels &amp; Agencies</h2>
      </div>
      <div className="flex gap-2 mb-4">
        <button data-testid="b2b-tab-pending" onClick={() => setSub("pending")}
          className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest border ${sub === "pending" ? "border-[#C5A059] text-[#C5A059]" : "border-white/10 text-zinc-500"}`}>
          Pending Applications{pending.length ? ` (${pending.length})` : ""}
        </button>
        <button data-testid="b2b-tab-active" onClick={() => setSub("active")}
          className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest border ${sub === "active" ? "border-[#C5A059] text-[#C5A059]" : "border-white/10 text-zinc-500"}`}>
          Active Partners ({partners.length})
        </button>
        {sub === "active" && (
          <button data-testid="b2b-add-partner" onClick={() => setAdding(true)}
            className="ml-auto px-4 h-9 rounded-full bg-[#C5A059] text-black text-xs uppercase tracking-widest font-semibold">
            + Add B2B Partner
          </button>
        )}
      </div>

      {sub === "pending" && (
        <div className="space-y-3">
          {pending.length === 0 && <p className="text-sm text-zinc-500">No pending applications.</p>}
          {pending.map((l) => (
            <div key={l.id} data-testid={`b2b-lead-${l.id}`} className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[220px]">
                <p className="text-white">{l.company_name} <span className="text-zinc-500 text-xs">OIB {l.oib || "—"}</span></p>
                <p className="text-xs text-zinc-400 mt-0.5">{l.contact_name} · {l.email} · {l.phone} · {l.est_monthly_rides} rides/mo</p>
                {l.notes && <p className="text-xs text-zinc-500 italic mt-0.5">"{l.notes}"</p>}
              </div>
              <button data-testid={`b2b-approve-${l.id}`} onClick={() => setApproving(l)}
                className="h-9 px-4 rounded-full bg-emerald-500/90 text-white text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Approve &amp; Convert
              </button>
              <button data-testid={`b2b-reject-${l.id}`} onClick={() => reject(l.id)}
                className="h-9 px-3 rounded-full border border-red-500/40 text-red-400 text-xs uppercase tracking-widest flex items-center gap-1"><X className="w-3.5 h-3.5" /> Reject</button>
            </div>
          ))}
        </div>
      )}

      {sub === "active" && (
        <div className="space-y-2">
          {partners.map((p) => (
            <div key={p.id} data-testid={`b2b-partner-${p.id}`} className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[220px]">
                <p className="text-white flex items-center gap-2">{p.company_name}
                  <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${p.active ? "border-emerald-400/50 text-emerald-300" : "border-red-400/50 text-red-300"}`}>{p.active ? "active" : "suspended"}</span>
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">{p.email} · {BILLING[p.billing_type] || "Monthly Invoice"}{p.oib ? ` · OIB ${p.oib}` : ""}</p>
                <span data-testid={`b2b-contract-status-${p.id}`}
                  className={`mt-1 inline-block text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${CONTRACT_BADGE[p.contract_status]?.cls || CONTRACT_BADGE.none.cls}`}>
                  Ugovor: {CONTRACT_BADGE[p.contract_status]?.label || CONTRACT_BADGE.none.label}
                </span>
              </div>
              <button data-testid={`b2b-contract-send-${p.id}`} onClick={() => sendContract(p)}
                disabled={contractBusy === p.id + "send"}
                className="h-9 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1">
                {contractBusy === p.id + "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                {p.contract_status === "Sent" || p.contract_status === "Expired" ? "Pošalji ponovno" : "Generiraj i pošalji ugovor"}
              </button>
              <button data-testid={`b2b-contract-view-${p.id}`} onClick={() => viewContract(p)}
                disabled={contractBusy === p.id + "view"}
                className="h-9 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1">
                {contractBusy === p.id + "view" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />} Pregledaj ugovor
              </button>
              {p.contract_status !== "Signed" && (
                <button data-testid={`b2b-contract-marksigned-${p.id}`} onClick={() => markContractSigned(p)}
                  disabled={contractBusy === p.id + "mark"}
                  className="h-9 px-3 rounded-full border border-emerald-500/40 text-emerald-300 text-xs uppercase tracking-widest flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Označi kao potpisano
                </button>
              )}
              <button data-testid={`b2b-billing-${p.id}`} onClick={() => setBillingPartner(p)}
                className="h-9 px-3 rounded-full border border-[#C5A059]/50 text-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Billing &amp; Invoices</button>
              <button data-testid={`b2b-reset-pin-${p.id}`} onClick={() => resetPin(p)} title="Reset PIN (emails new PIN)"
                className="h-9 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Reset PIN</button>
              <button data-testid={`b2b-toggle-${p.id}`} onClick={() => toggleActive(p)}
                className={`h-9 px-3 rounded-full border text-xs uppercase tracking-widest flex items-center gap-1 ${p.active ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400"}`}>
                <Power className="w-3.5 h-3.5" /> {p.active ? "Deactivate" : "Activate"}
              </button>
              {!p.active && (
                <button data-testid={`b2b-delete-${p.id}`} onClick={() => setDeleting(p)}
                  className="h-9 px-3 rounded-full bg-red-600/90 hover:bg-red-600 text-white text-xs uppercase tracking-widest font-semibold flex items-center gap-1 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Obriši
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {deleting && (
        <Modal onClose={() => setDeleting(null)}>
          <div data-testid="b2b-delete-confirm-modal">
            <div className="flex items-center gap-2 mb-3">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h3 className="text-lg text-white font-light">Trajno brisanje partnera</h3>
            </div>
            <p className="text-sm text-zinc-300 mb-2">
              Jeste li sigurni da želite <span className="text-red-400 font-semibold">trajno obrisati</span> B2B partnera <span className="text-white font-medium">{deleting.company_name}</span>?
            </p>
            <p className="text-xs text-zinc-500 mb-5">Ova radnja briše profil partnera i podatke o ugovoru iz baze te se ne može poništiti. Povijest vožnji i izdani računi ostaju sačuvani.</p>
            <div className="flex gap-3">
              <button data-testid="b2b-delete-cancel" onClick={() => setDeleting(null)}
                className="flex-1 h-11 rounded-xl border border-white/15 text-zinc-300 text-sm uppercase tracking-widest">Odustani</button>
              <button data-testid="b2b-delete-confirm" onClick={deletePartner} disabled={deleteBusy}
                className="flex-1 h-11 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold text-sm uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                {deleteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Trajno obriši
              </button>
            </div>
          </div>
        </Modal>
      )}
      {approving && <ApproveModal lead={approving} onClose={() => setApproving(null)} onDone={() => { setApproving(null); load(); }} />}
      {adding && <AddPartnerModal onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {billingPartner && <BillingModal partner={billingPartner} onClose={() => setBillingPartner(null)} />}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0d0d0d] border border-white/10 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;
const firstOfMonth = () => new Date().toISOString().slice(0, 8) + "01";
const today = () => new Date().toISOString().slice(0, 10);

function BillingModal({ partner, onClose }) {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [preview, setPreview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);

  const loadInvoices = () =>
    api.get(`/admin/partners/${partner.id}/invoices`).then(({ data }) => setInvoices(data || [])).catch(() => {});

  useEffect(() => { loadInvoices(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    if (!start || !end) return;
    api.get(`/admin/partners/${partner.id}/billing-preview`, { params: { start, end } })
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/partners/${partner.id}/invoices`, { start, end });
      toast.success(`Račun ${data.invoice_number} generiran (${eur(data.gross_eur)})`);
      loadInvoices();
      setPreview(null);
      api.get(`/admin/partners/${partner.id}/billing-preview`, { params: { start, end } })
        .then(({ data: d }) => setPreview(d)).catch(() => {});
    } catch (e) { toast.error(errMsg(e) || "Generiranje računa nije uspjelo"); }
    finally { setBusy(false); }
  };

  const openPdf = async (inv) => {
    setRowBusy(inv.id + "pdf");
    try {
      const { data } = await api.get(`/admin/b2b-invoices/${inv.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF nije dostupan"); }
    finally { setRowBusy(null); }
  };

  const sendEmail = async (inv) => {
    setRowBusy(inv.id + "mail");
    try {
      const { data } = await api.post(`/admin/b2b-invoices/${inv.id}/send-email`);
      toast.success(`Račun poslan na ${data.sent_to}`);
      loadInvoices();
    } catch (e) { toast.error(errMsg(e) || "Slanje nije uspjelo"); }
    finally { setRowBusy(null); }
  };

  const markPaid = async (inv) => {
    setRowBusy(inv.id + "paid");
    try {
      await api.post(`/admin/b2b-invoices/${inv.id}/mark-paid`);
      toast.success("Označeno kao PLAĆENO (Transakcijski)");
      loadInvoices();
    } catch (e) { toast.error(errMsg(e) || "Nije uspjelo"); }
    finally { setRowBusy(null); }
  };

  const statusCls = (s) =>
    s?.startsWith("PLAĆENO")
      ? "border-emerald-400/50 text-emerald-300"
      : "border-amber-400/50 text-amber-300";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="b2b-billing-modal" className="w-full max-w-3xl my-8 bg-[#0d0d0d] border border-white/10 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-lg text-white font-light">Billing &amp; Invoices — {partner.company_name}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">{partner.email}{partner.oib ? ` · OIB ${partner.oib}` : ""}</p>
          </div>
          <button onClick={onClose} data-testid="billing-modal-close" className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/30 p-4 mb-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Razdoblje obračuna</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Od</label>
              <input type="date" data-testid="billing-start" value={start} onChange={(e) => setStart(e.target.value)} className={FIELD} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Do</label>
              <input type="date" data-testid="billing-end" value={end} onChange={(e) => setEnd(e.target.value)} className={FIELD} />
            </div>
            <div className="flex-1 min-w-[180px]" data-testid="billing-preview">
              {preview ? (
                <p className="text-sm text-zinc-300">
                  <span className="text-white font-medium">{preview.ride_count}</span> nefakturiranih vožnji ·{" "}
                  <span className="text-[#C5A059] font-medium">{eur(preview.gross_eur)}</span>
                  {preview.vat_registered && (
                    <span className="text-xs text-zinc-500"> (osnovica {eur(preview.net_eur)} + PDV {preview.vat_rate_pct}% {eur(preview.vat_eur)})</span>
                  )}
                </p>
              ) : (
                <p className="text-xs text-zinc-600">Odaberite razdoblje…</p>
              )}
            </div>
            <button data-testid="billing-generate" onClick={generate}
              disabled={busy || !preview || !preview.ride_count}
              className="h-10 px-5 rounded-full bg-[#C5A059] text-black text-xs uppercase tracking-widest font-semibold disabled:opacity-40 flex items-center gap-2">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Generiraj R-1 Račun (PDF)
            </button>
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Izdani računi</p>
        <div className="space-y-2 max-h-[320px] overflow-y-auto">
          {invoices.length === 0 && <p className="text-sm text-zinc-600">Nema izdanih računa.</p>}
          {invoices.map((inv) => (
            <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="rounded-xl border border-white/10 bg-black/30 p-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm text-white">
                  Račun {inv.invoice_number}
                  <span data-testid={`invoice-status-${inv.id}`} className={`ml-2 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${statusCls(inv.status)}`}>{inv.status}</span>
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {inv.period_start} – {inv.period_end} · {inv.ride_count} vožnji · <span className="text-zinc-300">{eur(inv.gross_eur)}</span>
                  {inv.emailed_at ? " · ✉ poslano" : ""}
                </p>
              </div>
              <button data-testid={`invoice-pdf-${inv.id}`} onClick={() => openPdf(inv)} disabled={rowBusy === inv.id + "pdf"}
                className="h-8 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-[10px] uppercase tracking-widest flex items-center gap-1">
                {rowBusy === inv.id + "pdf" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />} PDF
              </button>
              <button data-testid={`invoice-email-${inv.id}`} onClick={() => sendEmail(inv)} disabled={rowBusy === inv.id + "mail"}
                className="h-8 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-[10px] uppercase tracking-widest flex items-center gap-1">
                {rowBusy === inv.id + "mail" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Pošalji e-mailom
              </button>
              {inv.status === "NEPLAĆENO" && (
                <button data-testid={`invoice-markpaid-${inv.id}`} onClick={() => markPaid(inv)} disabled={rowBusy === inv.id + "paid"}
                  className="h-8 px-3 rounded-full border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-widest flex items-center gap-1">
                  {rowBusy === inv.id + "paid" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Landmark className="w-3 h-3" />} Označi kao plaćeno (IBAN)
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ApproveModal({ lead, onClose, onDone }) {
  const [address, setAddress] = useState("");
  const [billing, setBilling] = useState("monthly_invoice");
  const [pin, setPin] = useState(String(Math.floor(100000 + Math.random() * 900000)));
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/b2b-leads/${lead.id}/approve`, { address, billing_type: billing, pin });
      toast.success(`${lead.company_name} is now a partner — welcome email sent with PIN ${pin}`);
      onDone();
    } catch (e) { toast.error(errMsg(e) || "Approve failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg text-white font-light mb-4">Approve {lead.company_name}</h3>
      <div className="space-y-3">
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Company address</label>
          <input data-testid="approve-address" value={address} onChange={(e) => setAddress(e.target.value)} className={FIELD} placeholder="Obala 12, 21000 Split" /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Billing method</label>
          <select data-testid="approve-billing" value={billing} onChange={(e) => setBilling(e.target.value)} className={FIELD}>
            <option value="monthly_invoice">Monthly Invoice</option>
            <option value="pay_in_vehicle">Guest Pays in Vehicle</option>
          </select></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Login PIN (4-6 digits)</label>
          <input data-testid="approve-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} className={FIELD} /></div>
        <button data-testid="approve-confirm" onClick={go} disabled={busy || pin.length < 4}
          className="w-full h-11 rounded-xl bg-[#C5A059] text-black font-semibold text-sm uppercase tracking-widest disabled:opacity-50">
          {busy ? "Creating…" : "Approve & send welcome email"}
        </button>
      </div>
    </Modal>
  );
}

function AddPartnerModal({ onClose, onDone }) {
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "", phone: "", oib: "", address: "", billing_type: "monthly_invoice", pin: String(Math.floor(100000 + Math.random() * 900000)) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/admin/partners", { ...f, send_welcome_email: true });
      toast.success(`Partner created — welcome email sent with PIN ${f.pin}`);
      onDone();
    } catch (e) { toast.error(errMsg(e) || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg text-white font-light mb-4">Add B2B Partner</h3>
      <div className="space-y-2.5">
        {[["company_name", "Company *"], ["contact_name", "Contact *"], ["email", "Email *"], ["phone", "Phone *"], ["oib", "OIB"], ["address", "Address"], ["pin", "PIN"]].map(([k, l]) => (
          <div key={k}><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{l}</label>
            <input data-testid={`add-partner-${k}`} value={f[k]} onChange={set(k)} className={FIELD} /></div>
        ))}
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Billing</label>
          <select data-testid="add-partner-billing" value={f.billing_type} onChange={set("billing_type")} className={FIELD}>
            <option value="monthly_invoice">Monthly Invoice</option>
            <option value="pay_in_vehicle">Guest Pays in Vehicle</option>
          </select></div>
        <button data-testid="add-partner-confirm" onClick={go} disabled={busy}
          className="w-full h-11 rounded-xl bg-[#C5A059] text-black font-semibold text-sm uppercase tracking-widest disabled:opacity-50">
          {busy ? "Creating…" : "Create & send welcome email"}
        </button>
      </div>
    </Modal>
  );
}
